import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/query-keys";
import { type UserResponse, usersApi } from "@/api/users";

/**
 * Hook to fetch a single user by ID
 *
 * Fetches combined user data from Kinde + MongoDB via the backend API.
 * Returns user data with loading and error states.
 *
 * @param userId - The Kinde user ID to fetch
 */
export const useUser = (userId: string | undefined) => {
	const {
		data: userData,
		error: userError,
		isError: isUserError,
		isLoading: isLoadingUser,
		refetch: refetchUser,
	} = useQuery<UserResponse, Error>({
		enabled: !!userId,
		queryFn: async () => {
			if (!userId) {
				throw new Error("User ID is required");
			}
			return await usersApi.getUser(userId);
		},
		queryKey: queryKeys.users.detail(userId ?? ""),
		// Don't retry 4xx (e.g. 400 missing org in JWT) — retries look like a "reload" and hide the error UI
		retry: (failureCount, error) => {
			if (
				error instanceof ApiError &&
				error.statusCode > 0 &&
				error.statusCode < 500
			) {
				return false;
			}
			return failureCount < 2;
		},
	});

	return {
		isLoadingUser,
		isUserError,
		refetchUser,
		userData,
		userError,
	};
};
